import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { tenantApi } from "../../services/api";

const QueueModal = ({ slug, isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState(0);
    const [hoveredBtn, setHoveredBtn] = useState(false);
    const [selectedType, setSelectedType] = useState("umum");
    const [isLoading, setIsLoading] = useState(false);
    const [createdQueue, setCreatedQueue] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedPoli, setSelectedPoli] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [poliList, setPoliList] = useState([]);
    const [dokterList, setDokterList] = useState([]);

    const getPoliPrefix = (poliName) => {
        const name = (poliName || "").toLowerCase();
        if (name.includes("umum")) return "A";
        if (name.includes("gigi")) return "C";
        if (name.includes("anak")) return "B";
        return name.charAt(0).toUpperCase() || "Q";
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
            fetchDynamicData();
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [isOpen, slug]);

    const fetchDynamicData = async () => {
        if (!slug) return;
        try {
            const [poliRes, dokterRes] = await Promise.all([
                tenantApi(slug).poli.getAll(),
                tenantApi(slug).dokter.getAll(),
            ]);
            if (poliRes.data?.success) setPoliList(poliRes.data.data);
            if (dokterRes.data?.success) setDokterList(dokterRes.data.data);
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    };

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isLoading) {
            resetAndClose();
        }
    };

    const resetAndClose = () => {
        onClose();
        setTimeout(() => {
            setStep(0);
            setOpenDropdown(null);
            setCreatedQueue(null);
            setSelectedPoli(null);
            setSelectedDoctor(null);
        }, 300);
    };

    const handleSubmit = async () => {
        if (!slug) {
            alert("Slug klinik tidak valid.");
            return;
        }
        if (!selectedPoli || !selectedDoctor) {
            alert("Silakan pilih Poliklinik dan Dokter terlebih dahulu.");
            return;
        }

        const userStr = localStorage.getItem("user");
        if (!userStr) {
            alert(
                "Anda belum login atau sesi Anda telah berakhir. Silakan login kembali.",
            );
            return;
        }

        const user = JSON.parse(userStr);
        const id_pasien = user?.pasien?.id_pasien || user?.id_pasien;
        if (!id_pasien) {
            alert("Data profil pasien tidak ditemukan. Silakan login kembali.");
            return;
        }

        const id_klinik =
            user?.klinik?.id_klinik ?? user?.pasien?.id_klinik ?? undefined;
        const dateObj = new Date();

        const payload = {
            ...(id_klinik != null ? { id_klinik } : {}),
            id_poli: selectedPoli.id_poli,
            id_pasien,
            id_dokter: selectedDoctor?.id_dokter || null,
            jenis_antrian: selectedType,
            tanggal: dateObj.toISOString().split("T")[0],
            jam: dateObj.toTimeString().split(" ")[0].substring(0, 5),
        };

        try {
            setIsLoading(true);
            const response = await tenantApi(slug).antrian.create(payload);
            if (response.data?.success) {
                setCreatedQueue(response.data.data);
                setStep(1);
            }
        } catch (error) {
            console.error("Error creating queue:", error);
            const msg =
                error.response?.data?.message ||
                "Gagal mengambil antrian. Pastikan semua data sudah dipilih dengan benar.";
            alert(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFinalClose = () => {
        if (onSuccess && createdQueue) {
            const prefix = getPoliPrefix(selectedPoli?.nama_poli);
            const queueNumber = `${prefix}${String(createdQueue.nomor_antrian).padStart(2, "0")}`;
            onSuccess({
                id_antrian: createdQueue.id_antrian,
                number: queueNumber,
                poly: selectedPoli?.nama_poli || "Poli Umum",
                doctor: selectedDoctor?.nama_dokter || "",
                type: selectedType === "umum" ? "Antrian Umum" : "Antrian BPJS",
                date: new Date().toLocaleDateString("id-ID", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                }),
                time: new Date().toLocaleTimeString("id-ID", {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                location: selectedPoli
                    ? `Ruang ${selectedPoli.nama_poli}`
                    : "Ruang Poli Umum",
            });
        }
        resetAndClose();
    };

    const toggleDropdown = (name) =>
        setOpenDropdown(openDropdown === name ? null : name);

    const filteredDokter = dokterList.filter(
        (d) => d.id_poli === selectedPoli?.id_poli,
    );

    const queueNumber = createdQueue
        ? `${getPoliPrefix(selectedPoli?.nama_poli)}${String(createdQueue.nomor_antrian).padStart(2, "0")}`
        : "...";

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={handleBackdropClick}
        >
            <style>{`
                .qm-scroll::-webkit-scrollbar { width: 4px; }
                .qm-scroll::-webkit-scrollbar-thumb { background: #06B6D4; border-radius: 99px; }
                .qm-scroll { scrollbar-width: thin; scrollbar-color: #06B6D4 transparent; }
            `}</style>

            <div className="bg-white w-full max-w-[420px] rounded-[20px] shadow-xl qm-scroll overflow-y-auto max-h-[90vh]">
                {/* ── Step 0: Form ── */}
                {step === 0 && (
                    <div className="p-6 sm:p-7">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-[17px] font-semibold text-gray-900">
                                Ambil nomor antrian
                            </h2>
                            <button
                                onClick={resetAndClose}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 hover:bg-cyan-100 text-gray-400 hover:text-cyan-400 transition-colors"
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                >
                                    <line x1="1" y1="1" x2="13" y2="13" />
                                    <line x1="13" y1="1" x2="1" y2="13" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Poliklinik */}
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Poliklinik
                                </label>
                                <div className="relative">
                                    <button
                                        onClick={() => toggleDropdown("poli")}
                                        className={`w-full h-11 px-4 flex items-center justify-between border rounded-xl text-sm transition-colors ${
                                            selectedPoli
                                                ? "text-gray-900 border-gray-200 bg-white hover:border-gray-300"
                                                : "text-gray-400 border-gray-200 bg-white hover:border-gray-300"
                                        }`}
                                    >
                                        <span>
                                            {selectedPoli
                                                ? selectedPoli.nama_poli
                                                : "Pilih poliklinik"}
                                        </span>
                                        <svg
                                            className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${openDropdown === "poli" ? "rotate-180" : ""}`}
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 6l4 4 4-4" />
                                        </svg>
                                    </button>
                                    {openDropdown === "poli" && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-10 overflow-hidden">
                                            {poliList.map((opt) => (
                                                <button
                                                    key={opt.id_poli}
                                                    onClick={() => {
                                                        setSelectedPoli(opt);
                                                        setSelectedDoctor(null);
                                                        setOpenDropdown(null);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                                                        selectedPoli?.id_poli ===
                                                        opt.id_poli
                                                            ? "text-cyan-600 font-medium bg-cyan-50"
                                                            : "text-gray-700"
                                                    }`}
                                                >
                                                    {opt.nama_poli}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Dokter */}
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Dokter
                                </label>
                                <div className="relative">
                                    <button
                                        onClick={() =>
                                            selectedPoli &&
                                            toggleDropdown("doctor")
                                        }
                                        disabled={!selectedPoli}
                                        className={`w-full h-11 px-4 flex items-center justify-between border rounded-xl text-sm transition-colors ${
                                            !selectedPoli
                                                ? "text-gray-300 border-gray-100 bg-gray-50 cursor-not-allowed"
                                                : selectedDoctor
                                                  ? "text-gray-900 border-gray-200 bg-white hover:border-gray-300"
                                                  : "text-gray-400 border-gray-200 bg-white hover:border-gray-300"
                                        }`}
                                    >
                                        <span className="truncate mr-2">
                                            {!selectedPoli
                                                ? "Pilih poliklinik terlebih dahulu"
                                                : selectedDoctor
                                                  ? selectedDoctor.nama_dokter
                                                  : "Pilih dokter"}
                                        </span>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {selectedDoctor?.jadwal && (
                                                <span className="text-[11px] text-gray-400">
                                                    {selectedDoctor.jadwal}
                                                </span>
                                            )}
                                            <svg
                                                className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${openDropdown === "doctor" ? "rotate-180" : ""}`}
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            >
                                                <path d="M4 6l4 4 4-4" />
                                            </svg>
                                        </div>
                                    </button>
                                    {openDropdown === "doctor" &&
                                        selectedPoli && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-10 overflow-hidden">
                                                {filteredDokter.length > 0 ? (
                                                    filteredDokter.map(
                                                        (opt) => (
                                                            <button
                                                                key={
                                                                    opt.id_dokter
                                                                }
                                                                onClick={() => {
                                                                    setSelectedDoctor(
                                                                        opt,
                                                                    );
                                                                    setOpenDropdown(
                                                                        null,
                                                                    );
                                                                }}
                                                                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                                                                    selectedDoctor?.id_dokter ===
                                                                    opt.id_dokter
                                                                        ? "text-cyan-600 font-medium bg-cyan-50"
                                                                        : "text-gray-700"
                                                                }`}
                                                            >
                                                                <span>
                                                                    {
                                                                        opt.nama_dokter
                                                                    }
                                                                </span>
                                                                {opt.jadwal && (
                                                                    <span className="text-[11px] text-gray-400 ml-2">
                                                                        {
                                                                            opt.jadwal
                                                                        }
                                                                    </span>
                                                                )}
                                                            </button>
                                                        ),
                                                    )
                                                ) : (
                                                    <p className="px-4 py-3 text-sm text-gray-400 italic text-center">
                                                        Tidak ada dokter di
                                                        poliklinik ini
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="h-px bg-gray-100" />

                            {/* Jenis Antrian */}
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                    Jenis antrian
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        {
                                            id: "umum",
                                            label: "Antrian umum",
                                            desc: "Pasien tanpa jaminan kesehatan.",
                                            icon: (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill={
                                                        selectedType === "umum"
                                                            ? "#0891b2"
                                                            : "#9ca3af"
                                                    }
                                                >
                                                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                                                </svg>
                                            ),
                                        },
                                        {
                                            id: "bpjs",
                                            label: "Antrian BPJS",
                                            desc: "Pasien terdaftar BPJS Kesehatan.",
                                            icon: (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill={
                                                        selectedType === "bpjs"
                                                            ? "#0891b2"
                                                            : "#9ca3af"
                                                    }
                                                >
                                                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zm-7-2h2v-2h-2v2zm0-4h2V8h-2v4zm-6 4h4v-2H7v2zm0-4h4V8H7v4z" />
                                                </svg>
                                            ),
                                        },
                                    ].map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() =>
                                                setSelectedType(t.id)
                                            }
                                            className={`p-3.5 rounded-xl border text-left transition-all ${
                                                selectedType === t.id
                                                    ? "border-cyan-200 bg-cyan-50"
                                                    : "border-gray-100 bg-white hover:border-gray-200"
                                            }`}
                                        >
                                            <div
                                                className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2.5 ${
                                                    selectedType === t.id
                                                        ? "bg-cyan-100"
                                                        : "bg-gray-100"
                                                }`}
                                            >
                                                {t.icon}
                                            </div>
                                            <p
                                                className={`text-[13px] font-semibold mb-0.5 ${selectedType === t.id ? "text-gray-900" : "text-gray-500"}`}
                                            >
                                                {t.label}
                                            </p>
                                            <p className="text-[11px] text-gray-400 leading-snug">
                                                {t.desc}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Submit */}
                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                onMouseEnter={() => setHoveredBtn("submit")}
                                onMouseLeave={() => setHoveredBtn(null)}
                                className={`w-full h-11 mt-1 text-white text-sm font-semibold rounded-xl transition-all active:scale-[0.99] ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                                style={{
                                    background:
                                        hoveredBtn === "submit"
                                            ? "linear-gradient(to right, #0891B2, #0e7490)"
                                            : "linear-gradient(to right, #06B6D4, #0891B2)",
                                }}
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg
                                            className="animate-spin"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="white"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                        >
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        Memproses...
                                    </span>
                                ) : (
                                    "Ambil nomor antrian"
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Step 1: Success ── */}
                {step === 1 && (
                    <div className="p-6 sm:p-8 flex flex-col items-center text-center">
                        {/* Icon */}
                        <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#16a34a"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>

                        <h2 className="text-[17px] font-semibold text-gray-900 mb-1">
                            Antrian berhasil diambil
                        </h2>
                        <p className="text-sm text-gray-400 max-w-[260px] leading-relaxed mb-5">
                            Nomor antrian Anda sudah aktif. Harap ikuti petunjuk
                            di bawah.
                        </p>

                        {/* Queue Badge */}
                        <div className="w-full bg-cyan-50 border border-cyan-100 rounded-2xl px-5 py-5 mb-5">
                            <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1.5">
                                Nomor antrian Anda
                            </p>
                            <p className="text-5xl font-bold text-cyan-600 tracking-tight mb-3">
                                {queueNumber}
                            </p>
                            <div className="h-px bg-cyan-100 mb-3" />
                            <p className="text-xs text-gray-400">
                                {selectedPoli?.nama_poli || "Poli Umum"}{" "}
                                &middot;{" "}
                                {new Date().toLocaleDateString("id-ID", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </p>
                            <p className="text-xs text-gray-400">
                                {new Date().toLocaleTimeString("id-ID", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </p>
                        </div>

                        {/* Tips */}
                        <div className="w-full text-left space-y-3 mb-6">
                            {[
                                "Datanglah ke klinik 15 menit sebelum nomor antrian dipanggil.",
                                "Pantau layar antrian di ruang tunggu.",
                                "Siapkan dokumen yang diperlukan, jika ada.",
                            ].map((tip, i) => (
                                <div key={i} className="flex gap-3 items-start">
                                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                        {i + 1}
                                    </span>
                                    <p className="text-[13px] text-gray-500 leading-snug">
                                        {tip}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={handleFinalClose}
                            onMouseEnter={() => setHoveredBtn("oke")}
                            onMouseLeave={() => setHoveredBtn(null)}
                            className="w-40 h-11 text-white text-sm font-semibold rounded-full transition-all active:scale-[0.99]"
                            style={{
                                background:
                                    hoveredBtn === "oke"
                                        ? "linear-gradient(to right, #0891B2, #0e7490)"
                                        : "linear-gradient(to right, #06B6D4, #0891B2)",
                            }}
                        >
                            Oke
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default QueueModal;
